import { useState } from 'react'
import { Skeleton } from '@indipia/ui-core'
import type { OrderVendorRow } from '../../api/orderApi'
import { useMaterialOrderData, useOrderActions } from '../../hooks/useMaterialOrders'
import { canRelease, isDirectOrderTask, orderErrorMessage } from '../../constants/materialOrder.logic'
import type { MaterialOrderChange } from '../../views/MaterialOrderPopup'
import { VendorPicker } from './VendorPicker'

// 캘린더(C-5) 날짜 선택 표의 '자재거래처' 칸 — 그 공종의 첫 번째 자재파트너를 바로 지정·변경·해제한다.
// 간트 상세창 자재파트너 카드와 같은 데이터(useMaterialOrderData 캐시 공유)·같은 콤보(VendorPicker)·같은 처리(assign/replace/release)를 쓴다.
// 직접발주가 아닌 공종(이 현장만 방식 반영)은 '발주 없음'. 거래처가 여러 곳이면 첫 곳만 바꾸고 '외 N곳'으로 알린다(나머지는 자재파트너배정 팝업에서).

interface Props {
  taskId: number
  siteId: number
  scheduleId: number
  disabled?: boolean
  /** 지정·변경·해제 성공 — 캘린더의 발주·입고 표시를 갱신(자재파트너배정 팝업 변경과 같은 처리) */
  onChanged?: (change: MaterialOrderChange) => void
}

const caption: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }

export function CalendarVendorCell({ taskId, siteId, scheduleId, disabled, onChanged }: Props) {
  const data = useMaterialOrderData(scheduleId)
  const actions = useOrderActions({ scheduleId, siteId })
  const [error, setError] = useState('')

  if (data.status === 'loading') return <Skeleton height={32} />
  if (data.status === 'error') return <span style={caption}>불러오지 못했어요</span>
  const taskOrder = data.tasks.find(t => t.taskId === taskId)
  if (!taskOrder || !isDirectOrderTask(data.settings, taskOrder)) return <span style={caption}>발주 없음</span>

  const rows: OrderVendorRow[] = data.rows.filter(r => r.taskId === taskId && r.orderStatusCode !== 'REPLACED')
  const row = rows[0] ?? null
  const notify = (next: OrderVendorRow | null, dates: { orderDate: string | null; deliveryDate: string | null }) =>
    onChanged?.({ taskId, vendorId: next?.vendorId ?? null, vendorName: next?.vendorName ?? null, ...dates })

  const handlePick = async (vendorId: number) => {
    setError('')
    try {
      const next = row ? (await actions.replace(row.orderVendorId, vendorId)).created : await actions.assign(taskId, vendorId)
      notify(next, next)
    } catch (err) {
      setError(orderErrorMessage(err, { vendorName: '' }))
    }
  }
  const handleRelease = async () => {
    if (!row) return
    setError('')
    try {
      await actions.release(row.orderVendorId, taskId)
      notify(rows[1] ?? null, { orderDate: row.orderDate, deliveryDate: row.deliveryDate })
    } catch (err) {
      setError(orderErrorMessage(err))
    }
  }

  return (
    <div className="min-w-0" onMouseDown={e => e.stopPropagation()}>
      <VendorPicker
        row={row}
        taskId={taskId}
        disabled={disabled}
        excludeVendorIds={rows.slice(1).map(r => r.vendorId)}
        showRegisterLink={false}
        onPick={vendorId => { void handlePick(vendorId) }}
        onRelease={row && canRelease(row) ? () => { void handleRelease() } : undefined}
      />
      {rows.length > 1 && <div className="mt-1" style={caption}>{`외 ${rows.length - 1}곳`}</div>}
      {error && <div role="alert" className="mt-1" style={{ ...caption, color: 'var(--status-danger-600)' }}>{error}</div>}
    </div>
  )
}
